<template>
  <div class="general-tab">
    <div v-if="isElement" class="mb-3">
      <label class="form-label">Field ID</label>
      <input
        type="text"
        class="form-control"
        :value="element?.general.id"
        @input="updateGeneralField('id', ($event.target as HTMLInputElement).value)"
      />
      <small class="form-text text-muted">Unique identifier for this field</small>
    </div>

    <div v-if="showField('label')" class="mb-3">
      <label class="form-label">Label</label>
      <textarea
        class="form-control"
        rows="2"
        :value="element?.general.label"
        @input="updateGeneralField('label', ($event.target as HTMLTextAreaElement).value)"
      />
    </div>

    <div v-if="showField('placeholder')" class="mb-3">
      <label class="form-label">Placeholder</label>
      <input
        type="text"
        class="form-control"
        :value="element?.general.placeholder"
        @input="updateGeneralField('placeholder', ($event.target as HTMLInputElement).value)"
      />
    </div>

    <div v-if="showField('helpText')" class="mb-3">
      <label class="form-label">Help Text</label>
      <textarea
        class="form-control"
        rows="2"
        :value="element?.general.helpText"
        @input="updateGeneralField('helpText', ($event.target as HTMLTextAreaElement).value)"
      />
    </div>

    <div v-if="showField('defaultValue')" class="mb-3">
      <label class="form-label">Default Value</label>
      <input
        type="text"
        class="form-control"
        :value="element?.general.defaultValue"
        @input="updateGeneralField('defaultValue', ($event.target as HTMLInputElement).value)"
      />
    </div>

    <div v-if="showField('required')" class="mb-3 form-check">
      <input
        type="checkbox"
        class="form-check-input"
        id="required"
        :checked="element?.general.required"
        @change="updateGeneralField('required', ($event.target as HTMLInputElement).checked)"
      />
      <label class="form-check-label" for="required">Required Field</label>
    </div>

    <div v-if="showField('disabled')" class="mb-3 form-check">
      <input
        type="checkbox"
        class="form-check-input"
        id="disabled"
        :checked="element?.general.disabled"
        @change="updateGeneralField('disabled', ($event.target as HTMLInputElement).checked)"
      />
      <label class="form-check-label" for="disabled">Disabled</label>
    </div>

    <div v-if="showField('readonly')" class="mb-3 form-check">
      <input
        type="checkbox"
        class="form-check-input"
        id="readonly"
        :checked="element?.general.readonly"
        @change="updateGeneralField('readonly', ($event.target as HTMLInputElement).checked)"
      />
      <label class="form-check-label" for="readonly">Read Only</label>
    </div>

    <div v-if="element?.elementType === 'todoList'" class="mb-3">
      <label class="form-label">List Type</label>
      <select class="form-select" :value="element.todoList?.mode" @change="updateTodoField('mode', ($event.target as HTMLSelectElement).value)">
        <option value="static">Static</option>
        <option value="dynamic">Dynamic</option>
      </select>
      <small class="form-text text-muted">Static rows allow completion and notes only. Dynamic lists allow submitters to add and remove rows.</small>
    </div>

    <div v-if="element?.elementType === 'todoList' && element.todoList?.mode === 'dynamic'" class="mb-3">
      <label class="form-label">Maximum Items</label>
      <input type="number" class="form-control" min="-1" :value="element.todoList?.maxItems ?? -1" @input="updateTodoMax(($event.target as HTMLInputElement).value)" />
      <small class="form-text text-muted">Use -1 for unlimited. The maximum cannot be less than the starter item count.</small>
    </div>

    <div v-if="element?.elementType === 'todoList'" class="mb-3">
      <label class="form-label">Starter Items</label>
      <div v-for="(item, index) in element.todoList?.starterItems" :key="item.id" class="border rounded p-2 mb-2">
        <div class="input-group mb-2">
          <input type="text" class="form-control" placeholder="TODO item" :value="item.text" @input="updateTodoItem(index, 'text', ($event.target as HTMLInputElement).value)" />
          <button class="btn btn-outline-secondary" type="button" title="Move up" :disabled="index === 0" @click="moveTodoItem(index, -1)">
            <i class="fas fa-arrow-up"></i>
          </button>
          <button class="btn btn-outline-secondary" type="button" title="Move down" :disabled="index === (element.todoList?.starterItems.length ?? 0) - 1" @click="moveTodoItem(index, 1)">
            <i class="fas fa-arrow-down"></i>
          </button>
          <button class="btn btn-outline-danger" type="button" @click="removeTodoItem(index)">
            <i class="fas fa-trash"></i>
          </button>
        </div>
        <textarea class="form-control" rows="2" placeholder="Optional description" :value="item.description" @input="updateTodoItem(index, 'description', ($event.target as HTMLTextAreaElement).value)"></textarea>
      </div>
      <button type="button" class="btn btn-sm btn-outline-primary" @click="addTodoItem">+ Add Starter Item</button>
    </div>

    <div v-if="element?.elementType === 'button'" class="mb-3">
      <label class="form-label">Button Type</label>
      <select class="form-select" :value="element.buttonType || 'submit'" @change="updateField('buttonType', ($event.target as HTMLSelectElement).value)">
        <option value="submit">Submit</option>
        <option value="reset">Reset</option>
        <option value="button">Button</option>
      </select>
    </div>

    <div v-if="showField('options')" class="mb-3">
      <label class="form-label">Options</label>
      <div v-for="(option, index) in element?.general.options" :key="index" class="input-group mb-2">
        <input
          type="text"
          class="form-control"
          placeholder="Label"
          :value="option.label"
          @input="updateOption(index, 'label', ($event.target as HTMLInputElement).value)"
        />
        <input
          type="text"
          class="form-control"
          placeholder="Value"
          :value="option.value"
          @input="updateOption(index, 'value', ($event.target as HTMLInputElement).value)"
        />
        <button class="btn btn-outline-danger" type="button" @click="removeOption(index)">
          <i class="fas fa-trash"></i>
        </button>
      </div>
      <button type="button" class="btn btn-sm btn-outline-primary" @click="addOption">
        + Add Option
      </button>
    </div>

    <div v-if="showField('rows')" class="mb-3">
      <label class="form-label">Rows</label>
      <input
        type="number"
        class="form-control"
        min="1"
        max="20"
        :value="element?.rows || 3"
        @input="updateField('rows', parseInt(($event.target as HTMLInputElement).value))"
      />
    </div>

    <div v-if="showField('min')" class="mb-3">
      <label class="form-label">Minimum Value</label>
      <input
        type="number"
        class="form-control"
        :value="element?.min"
        @input="updateField('min', parseFloat(($event.target as HTMLInputElement).value))"
      />
    </div>

    <div v-if="showField('max')" class="mb-3">
      <label class="form-label">Maximum Value</label>
      <input
        type="number"
        class="form-control"
        :value="element?.max"
        @input="updateField('max', parseFloat(($event.target as HTMLInputElement).value))"
      />
    </div>

    <div v-if="showField('step')" class="mb-3">
      <label class="form-label">Step</label>
      <input
        type="number"
        class="form-control"
        :value="element?.step || 1"
        @input="updateField('step', parseFloat(($event.target as HTMLInputElement).value))"
      />
    </div>

    <div v-if="component.type === 'column'" class="mb-3">
      <label class="form-label">Column Width (1-12)</label>
      <input
        type="number"
        class="form-control"
        min="1"
        max="12"
        :value="(component as Column).width"
        @input="updateColumnWidth(($event.target as HTMLInputElement).value)"
      />
    </div>

    <div v-if="component.type === 'container'" class="mb-3">
      <label class="form-label">Container Title</label>
      <input
        type="text"
        class="form-control"
        :value="(component as Container).title"
        @input="updateContainerField('title', ($event.target as HTMLInputElement).value)"
      />
    </div>

    <div v-if="component.type === 'container'" class="mb-3 form-check">
      <input
        type="checkbox"
        class="form-check-input"
        id="collapsible"
        :checked="(component as Container).collapsible"
        @change="updateContainerField('collapsible', ($event.target as HTMLInputElement).checked)"
      />
      <label class="form-check-label" for="collapsible">Collapsible</label>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { FormComponent, Element, Column, Container } from '../../types/form-builder';

interface Props {
  component: FormComponent;
}

interface Emits {
  (e: 'update', updates: Partial<FormComponent>): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const isElement = computed(() => props.component.type === 'element');
const element = computed(() => isElement.value ? props.component as Element : null);

function showField(field: string): boolean {
  if (props.component.type === 'container') {
    return false;
  }

  if (props.component.type === 'column') {
    return false;
  }

  const el = props.component as Element;
  const inputTypes = ['text', 'email', 'number', 'phone'];
  const textareaTypes = ['textarea'];
  const selectTypes = ['select', 'multiselect', 'radio', 'checkbox', 'checkboxGroup'];
  const numberTypes = ['number', 'slider', 'rating'];

  switch (field) {
    case 'label':
      return true;
    case 'placeholder':
      return [...inputTypes, ...textareaTypes, 'select'].includes(el.elementType);
    case 'helpText':
      return true;
    case 'defaultValue':
      return [...inputTypes, ...textareaTypes, ...numberTypes].includes(el.elementType);
    case 'required':
    case 'disabled':
    case 'readonly':
      return [...inputTypes, ...textareaTypes, ...selectTypes, ...numberTypes, 'date', 'time', 'datetime', 'file'].includes(el.elementType);
    case 'options':
      return selectTypes.includes(el.elementType);
    case 'rows':
      return textareaTypes.includes(el.elementType);
    case 'min':
    case 'max':
    case 'step':
      return numberTypes.includes(el.elementType);
    default:
      return false;
  }
}

function updateField(field: string, value: any) {
  emit('update', { [field]: value });
}

function updateGeneralField(field: string, value: any) {
  const el = props.component as Element;
  emit('update', {
    general: {
      ...el.general,
      [field]: value,
    },
  });
}

function updateTodoField(field: string, value: any) {
  const el = props.component as Element;
  emit('update', {
    todoList: {
      mode: 'static',
      maxItems: -1,
      starterItems: [],
      ...el.todoList,
      [field]: value,
    },
  });
}

function updateTodoMax(value: string) {
  const el = props.component as Element;
  const count = el.todoList?.starterItems.length || 0;
  const parsed = parseInt(value, 10);
  updateTodoField('maxItems', parsed === -1 ? -1 : Math.max(count, parsed || count || 1));
}

function addTodoItem() {
  const el = props.component as Element;
  const starterItems = [...(el.todoList?.starterItems || []), {
    id: `todo_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    text: '',
    description: '',
  }];
  const maxItems = el.todoList?.maxItems ?? -1;
  emit('update', {
    todoList: {
      mode: el.todoList?.mode || 'static',
      maxItems: maxItems === -1 ? -1 : Math.max(maxItems, starterItems.length),
      starterItems,
    },
  });
}

function updateTodoItem(index: number, field: 'text' | 'description', value: string) {
  const el = props.component as Element;
  const starterItems = [...(el.todoList?.starterItems || [])];
  starterItems[index] = { ...starterItems[index], [field]: value };
  updateTodoField('starterItems', starterItems);
}

function removeTodoItem(index: number) {
  const el = props.component as Element;
  const starterItems = [...(el.todoList?.starterItems || [])];
  starterItems.splice(index, 1);
  updateTodoField('starterItems', starterItems);
}

function moveTodoItem(index: number, direction: -1 | 1) {
  const el = props.component as Element;
  const starterItems = [...(el.todoList?.starterItems || [])];
  const newIndex = index + direction;
  if (newIndex < 0 || newIndex >= starterItems.length) return;
  const [item] = starterItems.splice(index, 1);
  starterItems.splice(newIndex, 0, item);
  updateTodoField('starterItems', starterItems);
}

function updateOption(index: number, field: 'label' | 'value', value: string) {
  const el = props.component as Element;
  const options = [...(el.general.options || [])];
  options[index] = { ...options[index], [field]: value };

  emit('update', {
    general: { ...el.general, options },
  });
}

function addOption() {
  const el = props.component as Element;
  const options = [...(el.general.options || [])];
  options.push({ label: `Option ${options.length + 1}`, value: `option${options.length + 1}` });

  emit('update', {
    general: { ...el.general, options },
  });
}

function removeOption(index: number) {
  const el = props.component as Element;
  const options = [...(el.general.options || [])];
  options.splice(index, 1);

  emit('update', {
    general: { ...el.general, options },
  });
}

function updateColumnWidth(value: string) {
  const width = parseInt(value, 10);
  if (width >= 1 && width <= 12) {
    emit('update', { width } as Partial<Column>);
  }
}

function updateContainerField(field: string, value: any) {
  emit('update', {
    [field]: value,
  });
}
</script>
